import { type Ref } from 'vue'

const fetchers = new Map<Ref, PromiseLike<unknown>>()

/**
 * Fetches data, but only once
 *
 * @param storage Reference holding the cache.
 * @param fetcher Will be invoked if storage.value is nullish, but not twice if running parallell
 * @returns storage.value
 */
export async function cached<type>(
  storage: Ref<type>,
  fetcher: () => PromiseLike<unknown>
): Promise<type> {
  return await cachedHasValue(storage, (val) => (val ? true : false), fetcher)
}

/**
 * Fetches data, but only once
 *
 * @param storage Reference holding the cache.
 * @param fetcher Will be invoked if storage.value is nullish or empty array, but not twice if running parallell
 * @returns storage.value
 */
export async function cachedRefetchEmptyArray<type>(
  storage: Ref<type[]>,
  fetcher: () => PromiseLike<unknown>
): Promise<type[]> {
  return await cachedHasValue(storage, (val) => val && val.length > 0, fetcher)
}

/**
 * Fetches data, but only once
 *
 * @param storage Reference holding the cache.
 * @param hasValue Determines if cache holds a value
 * @param fetcher Will be invoked if cache does not hold a value, but not twice if running parallell
 * @returns storage.value
 */
export async function cachedHasValue<type>(
  storage: Ref<type>,
  hasValue: (val: type) => boolean,
  fetcher: () => PromiseLike<unknown>
): Promise<type> {
  if (hasValue(storage.value)) {
    return storage.value
  }

  let promise: PromiseLike<unknown>
  if (fetchers.has(storage)) {
    promise = fetchers.get(storage)!
  } else {
    promise = fetcher()
    fetchers.set(storage, promise)
    await promise
    fetchers.delete(storage)
  }
  await promise

  return storage.value
}
